import { Eye, EyeOff, ImageIcon, KeyRound, Languages, Trash2, UserCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useI18n } from '../contexts/I18nContext';
import { ApiError } from '../lib/api';
import * as authApi from '../lib/authApi';
import type { Locale } from '../lib/i18n';
import { useAuthStore } from '../store/useAuthStore';
import { PushNotificationsSettings } from '../components/settings/PushNotificationsSettings';
import { CompanyLogoField } from '../components/settings/CompanyLogoField';
import { ConnectedAppsSection } from '../components/mcp/ConnectedApps';
import { useConfirm } from '../components/ui/ConfirmProvider';

// ---------------------------------------------------------------------------
// Profile page (CLE-434).
//
// Reached from the sidebar "Profil" link (above the logout button). Shows the
// current user's first name, last name and email, a language selector (RO/EN)
// that used to live in the sidebar, and a change-password form. The language
// choice persists to localStorage via useI18n().setLocale and applies app-wide
// immediately.
// ---------------------------------------------------------------------------

/** Mirrors the backend isStrongPassword rule: ≥8 chars, an uppercase and a digit. */
function isStrongPassword(password: string): boolean {
  return password.length >= 8 && /[A-Z]/.test(password) && /\d/.test(password);
}

const LANGUAGE_OPTIONS: { code: Locale; flag: string; label: string }[] = [
  { code: 'ro', flag: '🇷🇴', label: 'RO' },
  { code: 'en', flag: '🇬🇧', label: 'EN' },
];

// The backend stores a single `name`. For display we split it into a first
// name (first token) and last name (the remainder) — a best-effort mapping to
// the Romanian "prenume / nume" fields the spec asks for.
function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

// Password input with an inline show/hide "eye" toggle.
function PasswordInput({
  label,
  value,
  autoComplete,
  onChange,
}: {
  label: string;
  value: string;
  autoComplete: string;
  onChange: (value: string) => void;
}) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="relative mt-1">
        <input
          type={visible ? 'text' : 'password'}
          required
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 pr-10 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? t('profile.password.hide') : t('profile.password.show')}
          title={visible ? t('profile.password.hide') : t('profile.password.show')}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground"
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </label>
  );
}

function ChangePasswordSection() {
  const { t } = useI18n();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    if (newPassword !== confirm) {
      setError(t('profile.password.mismatch'));
      return;
    }
    if (!isStrongPassword(newPassword)) {
      setError(t('profile.password.weak'));
      return;
    }

    const accessToken = useAuthStore.getState().accessToken;
    if (!accessToken) {
      setError(t('profile.password.failed'));
      return;
    }

    setSubmitting(true);
    try {
      await authApi.changePassword(accessToken, { currentPassword, newPassword });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVALID_CREDENTIALS') {
        setError(t('profile.password.wrongCurrent'));
      } else {
        setError(err instanceof Error ? err.message : t('profile.password.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="mt-5 rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <KeyRound size={18} className="text-muted-foreground" />
        <div>
          <h2 className="text-sm font-semibold text-foreground">{t('profile.password.title')}</h2>
          <p className="text-xs text-muted-foreground">{t('profile.password.subtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}
        {success && (
          <p
            role="status"
            className="rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2 text-sm text-green-600 dark:text-green-400"
          >
            {t('profile.password.success')}
          </p>
        )}

        <PasswordInput
          label={t('profile.password.current')}
          value={currentPassword}
          autoComplete="current-password"
          onChange={setCurrentPassword}
        />
        <PasswordInput
          label={t('profile.password.new')}
          value={newPassword}
          autoComplete="new-password"
          onChange={setNewPassword}
        />
        <PasswordInput
          label={t('profile.password.confirm')}
          value={confirm}
          autoComplete="new-password"
          onChange={setConfirm}
        />

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {submitting ? t('profile.password.submitting') : t('profile.password.submit')}
        </button>
      </form>
    </section>
  );
}

/**
 * „Cere ștergerea contului” (Google Play): the password, then a danger
 * confirmation; the account is deactivated at once and the firm's admins
 * finish the deletion. The user is signed out right after.
 */
function DeleteAccountSection() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const logout = useAuthStore((s) => s.logout);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const accessToken = useAuthStore.getState().accessToken;
    if (!password || !accessToken) return;
    const ok = await confirm({
      title: t('profile.deleteAccount.confirmTitle'),
      message: t('profile.deleteAccount.confirmMessage'),
      confirmLabel: t('profile.deleteAccount.confirmLabel'),
      danger: true,
    });
    if (!ok) return;

    setSubmitting(true);
    try {
      await authApi.requestAccountDeletion(accessToken, password);
      logout();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVALID_CREDENTIALS') {
        setError(t('profile.deleteAccount.wrongPassword'));
      } else if (err instanceof ApiError && err.code === 'LAST_ADMIN') {
        setError(t('profile.deleteAccount.lastAdmin'));
      } else {
        setError(err instanceof Error ? err.message : t('profile.deleteAccount.failed'));
      }
      setSubmitting(false);
    }
  }

  return (
    <section className="mt-5 rounded-lg border border-destructive/30 bg-card p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Trash2 size={18} className="text-destructive" />
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            {t('profile.deleteAccount.title')}
          </h2>
          <p className="text-xs text-muted-foreground">{t('profile.deleteAccount.subtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('profile.deleteAccount.explain')}{' '}
          <Link to="/terms#stergere-cont" className="text-brand-600 underline">
            {t('profile.deleteAccount.details')}
          </Link>
        </p>
        {error && (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}
        <PasswordInput
          label={t('profile.deleteAccount.password')}
          value={password}
          autoComplete="current-password"
          onChange={setPassword}
        />
        <button
          type="submit"
          disabled={submitting || !password}
          className="ui-btn-danger disabled:opacity-50"
        >
          {submitting ? t('profile.deleteAccount.submitting') : t('profile.deleteAccount.submit')}
        </button>
      </form>
    </section>
  );
}

export function ProfilePage() {
  const { t, locale, setLocale } = useI18n();
  const user = useAuthStore((s) => s.user);

  const { firstName, lastName } = splitName(user?.name ?? '');
  const notProvided = t('profile.notProvided');

  return (
    <div className="mx-auto max-w-2xl p-6 sm:p-8">
      <header className="mb-6 flex items-center gap-3">
        {user?.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt=""
            className="h-11 w-11 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <UserCircle size={26} />
          </span>
        )}
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('profile.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('profile.subtitle')}</p>
        </div>
      </header>

      <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
        <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label={t('profile.firstName')} value={firstName || notProvided} />
          <Field label={t('profile.lastName')} value={lastName || notProvided} />
          <div className="sm:col-span-2">
            <Field label={t('profile.email')} value={user?.email || notProvided} />
          </div>
        </dl>
      </section>

      <AvatarSection />

      <section className="mt-5 rounded-lg border border-border bg-card p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Languages size={18} className="text-muted-foreground" />
          <div>
            <h2 className="text-sm font-semibold text-foreground">{t('profile.language')}</h2>
            <p className="text-xs text-muted-foreground">{t('profile.languageHint')}</p>
          </div>
        </div>
        <div
          role="radiogroup"
          aria-label={t('profile.language')}
          className="inline-flex overflow-hidden rounded-md border border-border"
        >
          {LANGUAGE_OPTIONS.map((opt) => {
            const active = locale === opt.code;
            return (
              <button
                key={opt.code}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setLocale(opt.code)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <span aria-hidden>{opt.flag}</span>
                {opt.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Push notifications are a per-person choice (what to be notified about, on
          which device), so they live here, on the page every role has, and nowhere
          else: Settings is administrative and permission-gated. */}
      <div className="mt-5">
        <PushNotificationsSettings />
      </div>

      <ConnectedAppsSection showGuideLink />

      <ChangePasswordSection />

      <DeleteAccountSection />
    </div>
  );
}

/**
 * Profile photo. Cropped square in the browser and stored inline on the user
 * (PUT /api/auth/me/avatar), like the company logo; every avatar in the app
 * (tasks, comments, chat, team) finds it through the team directory, so the
 * caches that carry users are refreshed after a change.
 */
function AvatarSection() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const updateUser = useAuthStore((s) => s.updateUser);
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (avatarUrl: string | null) => authApi.updateAvatar(accessToken ?? '', avatarUrl),
    onSuccess: (saved) => {
      setError(null);
      updateUser({ avatarUrl: saved.avatarUrl });
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['team-chat'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Poza nu a putut fi salvată.'),
  });
  return (
    <section className="mt-5 rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <ImageIcon size={18} className="text-muted-foreground" />
        <div>
          <h2 className="text-sm font-semibold text-foreground">Poza de profil</h2>
          <p className="text-xs text-muted-foreground">
            Apare lângă numele tău peste tot: sarcini, comentarii, chat, echipă.
          </p>
        </div>
      </div>
      <CompanyLogoField
        kind="avatar"
        value={user?.avatarUrl ?? ''}
        onChange={(dataUrl) => save.mutate(dataUrl || null)}
        disabled={save.isPending}
      />
      {save.isPending && <p className="mt-2 text-xs text-muted-foreground">Se salvează…</p>}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </section>
  );
}
